import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Phone, MapPin, Star } from 'lucide-react';
import ProductCard from './ProductCard';
import { Button } from './ui/button';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

/**
 * Composant générique pour landing pages SEO local par usage.
 * Exemples : "Caméra parking Montpellier", "Alarme commerce Montpellier", etc.
 *
 * Props requis :
 * - seoTitle : <title> SEO
 * - seoDescription : <meta description>
 * - canonical : URL canonique
 * - h1 : titre H1 visible
 * - subtitle : phrase d'accroche
 * - city : ville visée (Montpellier, Nîmes...)
 * - usage : usage cible (parking, commerce, maison...)
 * - heroBadge : petit badge en haut du hero (ex: "Vidéosurveillance professionnelle")
 * - benefits : array de { icon, title, desc }
 * - faqs : array de { q, a }
 * - productFilter : fonction (product) => boolean pour sélectionner les produits adaptés
 * - phoneNumber : numéro pour bouton CTA
 */
const SeoUsageLandingPage = ({
  seoTitle,
  seoDescription,
  canonical,
  h1,
  subtitle,
  city = 'Montpellier',
  usage = '',
  heroBadge,
  benefits = [],
  faqs = [],
  productFilter,
  phoneNumber = '07 45 04 09 29',
  recommendationsTitle = 'Produits recommandés pour votre usage'
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch(`${API}/products`);
        if (res.ok) {
          const all = await res.json();
          setProducts(productFilter ? all.filter(productFilter) : all.slice(0, 6));
        }
      } catch (err) {
        console.error('Failed to fetch products', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [productFilter]);

  // FAQ Schema.org JSON-LD
  const faqJsonLd = faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  } : null;

  // LocalBusiness Schema
  const localBusinessJsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "FGS Alarme Montpellier",
    "areaServed": { "@type": "City", "name": city },
    "telephone": phoneNumber,
    "priceRange": "€€",
    "address": { "@type": "PostalAddress", "addressLocality": city, "addressCountry": "FR" }
  };

  return (
    <div className="min-h-screen bg-black" data-testid="seo-usage-landing">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        {canonical && <link rel="canonical" href={canonical} />}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        {canonical && <meta property="og:url" content={canonical} />}
        <script type="application/ld+json">{JSON.stringify(localBusinessJsonLd)}</script>
        {faqJsonLd && <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>}
      </Helmet>

      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 pb-12">
        <div className="text-center max-w-3xl mx-auto">
          {heroBadge && (
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-purple-500/10 text-purple-300 border border-purple-500/20 mb-4">
              <MapPin className="w-3.5 h-3.5" /> {heroBadge}
            </span>
          )}
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-4 tracking-tight">
            {h1}
          </h1>
          <p className="text-white/60 text-base md:text-lg mb-8 leading-relaxed">
            {subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={`tel:${phoneNumber.replace(/\s/g, '')}`}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold transition-colors"
              data-testid="seo-cta-call"
            >
              <Phone className="w-4 h-4" />
              Appeler : {phoneNumber}
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white/5 border border-white/10 text-white hover:bg-white/10 font-semibold transition-colors"
              data-testid="seo-cta-quote"
            >
              Demander un devis gratuit
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      {benefits.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16">
          <h2 className="text-2xl font-semibold text-white text-center mb-10 tracking-tight">
            Pourquoi choisir notre solution pour votre {usage} à {city} ?
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {benefits.map((b, i) => (
              <div key={i} className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
                <div className="w-11 h-11 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-4">
                  <b.icon className="w-5 h-5 text-emerald-400" />
                </div>
                <h3 className="text-white font-semibold mb-2">{b.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* RECOMMENDED PRODUCTS */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <h2 className="text-2xl font-semibold text-white tracking-tight">
            {recommendationsTitle}
          </h2>
          <Link to="/video" className="text-emerald-400 hover:text-emerald-300 text-sm flex items-center gap-1">
            Voir tout le catalogue <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white/[0.02] border border-white/5 rounded-xl h-96 animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <p className="text-white/60 text-center py-12">Aucun produit ne correspond pour l'instant.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.slice(0, 8).map(p => <ProductCard key={p.product_id} product={p} />)}
          </div>
        )}
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="max-w-[900px] mx-auto px-6 md:px-12 mb-16">
          <h2 className="text-2xl font-semibold text-white text-center mb-8 tracking-tight">
            Questions fréquentes
          </h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details
                key={i}
                className="group bg-white/[0.02] border border-white/5 rounded-xl p-5 cursor-pointer"
              >
                <summary className="flex items-center justify-between text-white font-medium list-none">
                  <span>{f.q}</span>
                  <span className="text-white/40 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="mt-3 text-white/70 text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="max-w-[1100px] mx-auto px-6 md:px-12 mb-16">
        <div className="bg-gradient-to-br from-purple-500/10 to-emerald-500/5 border border-purple-500/20 rounded-3xl p-8 md:p-12 text-center">
          <Star className="w-10 h-10 text-amber-400 mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-semibold text-white mb-3 tracking-tight">
            Parlons de votre projet à {city}
          </h2>
          <p className="text-white/60 mb-6 max-w-2xl mx-auto">
            Un installateur certifié Ajax se déplace gratuitement pour évaluer vos besoins et vous proposer la solution adaptée.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={`tel:${phoneNumber.replace(/\s/g, '')}`}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold"
            >
              <Phone className="w-4 h-4" />
              {phoneNumber}
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white text-zinc-950 hover:bg-white/90 font-semibold"
            >
              Demande de devis
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SeoUsageLandingPage;
